import type { FleetMachine, FleetMachineMetrics } from "@/context/fleet"
import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse, HttpError } from "@/transport/http"
import { decodeE2EPublicKey } from "@/transport/e2e"
import { invalidateHealthyRoute, recordHealthyRoute } from "@/transport/health-probe-cache"
import { abortable } from "@/utils/abortable"
import { requestHealth, type HealthProbeFailure, type HealthProbeResult } from "@/transport/health"
export type { HealthProbeFailure, HealthProbeResult } from "@/transport/health"

const publicRouteProviders = ["zrok", "cloudflare", "opentunnel"] as const
export type PublicMachineRoute = { provider: typeof publicRouteProviders[number]; url: string }
const machineRoutePolicies = ["cloudflare-only", "opentunnel-only"] as const
export type MachineRoutePolicy = typeof machineRoutePolicies[number]

function isPublicRouteProvider(value: unknown): value is PublicMachineRoute["provider"] {
  return publicRouteProviders.some(provider => provider === value)
}

function decodePublicRoutes(value: unknown): PublicMachineRoute[] | undefined {
  if (value === undefined) return undefined
  const routes = jsonArray(value)
  if (routes.length > publicRouteProviders.length) throw new Error("Server returned too many machine routes")
  const providers = new Set<string>()
  return routes.map(value => {
    const route = jsonObject(value, "Server returned an invalid route")
    const provider = route.provider
    if (!isPublicRouteProvider(provider) || providers.has(provider)) throw new Error("Invalid route provider")
    providers.add(provider)
    const url = new URL(jsonString(route.url, "Invalid route URL"))
    if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash || url.pathname !== "/") throw new Error("Invalid route URL")
    return { provider, url: url.origin }
  })
}

export function decodeMachine(value: unknown): FleetMachine {
  const machine = jsonObject(value, "Server returned an invalid machine")
  jsonString(machine.name, "Server returned an invalid machine")
  jsonString(machine.public_url, "Server returned an invalid machine")
  jsonNumber(machine.updated_at, "Server returned an invalid machine")
  if (machine.route_policy !== undefined && !machineRoutePolicies.some(policy => policy === machine.route_policy)) throw new Error("Invalid machine route policy")
  if (machine.e2e_public_key !== undefined) decodeE2EPublicKey(jsonString(machine.e2e_public_key, "Invalid machine encryption key"))
  return { ...machine, public_routes: decodePublicRoutes(machine.public_routes) } as FleetMachine
}

function decodeMetrics(value: unknown): FleetMachineMetrics {
  const metrics = jsonObject(value, "Server returned invalid machine metrics")
  jsonString(metrics.name, "Server returned invalid machine metrics")
  return metrics as FleetMachineMetrics
}

export type MachineRouteReport = {
  kind?: "comparison" | "transition"
  trigger?: "initial" | "routes-changed" | "retry" | "network-error" | "gateway-error" | "health-check" | "sse-error" | "sse-stall" | "pty-error" | "request-stall" | "local-discovery" | "preferred-route"
  previous?: "none" | "direct-local" | "direct-public"
  previous_provider?: PublicMachineRoute["provider"]
  client_id?: string
  client_sequence?: number
  client_time_ms?: number
  client_elapsed_ms?: number
  started_elapsed_ms?: number
  visibility?: "visible" | "hidden" | "unknown"
  online?: boolean
  comparison_id?: string
  round?: 1 | 2
  machine: string
  selected: "direct-local" | "direct-public" | "none"
  selected_provider?: PublicMachineRoute["provider"]
  attempts: Array<{
    route: "direct-local" | "direct-public"
    provider?: PublicMachineRoute["provider"]
    result: "success" | "unavailable"
    latency_ms: number
    reason?: HealthProbeFailure["reason"]
    http_status?: number
  }>
}

export function reportMachineRouteSelection(report: MachineRouteReport, signal?: AbortSignal) {
  return controlApiFetch("/api/machine-route-events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(report),
    keepalive: true,
    signal,
  })
}

export async function probeHealth(
  url: string,
  options?: { signal?: AbortSignal; accessToken?: string; expectedMachineName?: string },
): Promise<HealthProbeResult> {
  const server = { url, accessToken: options?.accessToken, expectedMachineName: options?.expectedMachineName }
  invalidateHealthyRoute(server)
  const result = await requestHealth(url, options)
  if (result.healthy) recordHealthyRoute(server)
  return result
}

export function updateMachineDisplayName(name: string, displayName: string) {
  return controlApiFetch(`/api/machines/${encodeURIComponent(name)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ display_name: displayName }),
  }).then(expectResponse)
}

export function transientMachineAccessFailure(error: unknown) {
  return error instanceof TypeError || (error instanceof Error && error.name === "TimeoutError") ||
    (error instanceof HttpError && (error.status === 408 || error.status === 429 || error.status >= 500))
}

export type MachineAccessGrant = { token: string; expires_at: number }

function decodeGrants(value: unknown, invalid: string) {
  const grants = new Map<string, MachineAccessGrant>()
  for (const item of jsonArray(value, invalid)) {
    const grant = jsonObject(item, invalid)
    grants.set(jsonString(grant.name, invalid), { token: jsonString(grant.token, invalid), expires_at: jsonNumber(grant.expires_at, invalid) })
  }
  return grants
}

/** The most machines one batch access request may name; the control plane rejects longer lists. */
const MACHINE_ACCESS_BATCH_LIMIT = 100

/**
 * Asks for several machines' access grants, in requests of at most
 * MACHINE_ACCESS_BATCH_LIMIT machines. Each named machine maps to its grant or
 * to the error the control plane reported for it; a request that fails maps
 * every machine it named to its error.
 */
export async function issueMachineAccessBatch(names: readonly string[], signal?: AbortSignal) {
  const chunks: (readonly string[])[] = []
  for (let start = 0; start < names.length; start += MACHINE_ACCESS_BATCH_LIMIT) {
    chunks.push(names.slice(start, start + MACHINE_ACCESS_BATCH_LIMIT))
  }
  const outcomes = await Promise.allSettled(chunks.map((chunk) => issueMachineAccessChunk(chunk, signal)))
  const result = new Map<string, MachineAccessGrant | Error>()
  for (const [index, outcome] of outcomes.entries()) {
    if (outcome.status === "fulfilled") {
      for (const [name, grant] of outcome.value) result.set(name, grant)
      continue
    }
    const error = outcome.reason instanceof Error ? outcome.reason : new Error(String(outcome.reason))
    for (const name of chunks[index]!) result.set(name, error)
  }
  return result
}

async function issueMachineAccessChunk(names: readonly string[], signal?: AbortSignal) {
  const response = await controlApiFetch("/api/machines/access", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ names }),
    signal,
  })
  return expectJson(response, (value) => {
    const invalid = "Server returned invalid machine access"
    const batch = jsonObject(value, invalid)
    const result = new Map<string, MachineAccessGrant | HttpError>(decodeGrants(batch.grants, invalid))
    for (const item of jsonArray(batch.errors, invalid)) {
      const error = jsonObject(item, invalid)
      result.set(jsonString(error.name, invalid), new HttpError(jsonNumber(error.status, invalid), jsonString(error.error, invalid)))
    }
    return result
  })
}

/** With `access`, the snapshot also carries a grant for each machine that can have one. */
export function getFleetBootstrap(options?: { access?: boolean }) {
  return controlApiFetch(options?.access ? "/api/fleet/bootstrap?access=all" : "/api/fleet/bootstrap")
    .then((response) => expectJson(response, (value) => {
      const invalid = "Server returned invalid fleet bootstrap"
      const snapshot = jsonObject(value, invalid)
      return {
        machines: jsonArray(snapshot.machines).map(decodeMachine),
        metrics: jsonArray(snapshot.metrics).map(decodeMetrics),
        events: jsonString(snapshot.events, invalid),
        ...(options?.access ? { access: decodeGrants(snapshot.access, invalid) } : {}),
      }
    }))
}

export function getMachineMetrics() {
  return controlApiFetch("/api/machines/metrics")
    .then((response) => expectJson(response, (value) => jsonArray(value).map(decodeMetrics)))
}

export async function removeMachine(name: string) {
  await expectResponse(await controlApiFetch(`/api/machines/${encodeURIComponent(name)}`, { method: "DELETE" }))
}

export function restartMachine(name: string) {
  return controlApiFetch(`/api/machines/${encodeURIComponent(name)}/restart`, { method: "POST" })
    .then(expectResponse)
}

export async function updateMachineBackend(name: string, signal?: AbortSignal) {
  const response = await controlApiFetch(`/api/machines/${encodeURIComponent(name)}/update-backend`, { method: "POST", signal })
  return expectJson(response, (value) => {
    const receipt = jsonObject(value, "Invalid machine update receipt")
    return {
      release: jsonString(receipt.release_id, "Missing update release"),
      generation: jsonNumber(receipt.generation, "Missing update attempt"),
    }
  })
}

export function getMachineUpdate(name: string, signal?: AbortSignal) {
  return controlApiFetch(`/api/machines/${encodeURIComponent(name)}/update-backend`, { signal, cache: "no-store" })
    .then((response) => expectJson(response, (value) => {
      const machine = decodeMachine(value)
      return { ...machine, update_generation: jsonNumber(machine.update_generation, "Missing update attempt") }
    }))
}

export const fleetStreamConnectTimeoutMs = 10_000
// The control plane sends a heartbeat comment every 45s; allow 15s for delivery jitter.
export const fleetStreamIdleTimeoutMs = 60_000

export async function streamFleetEvents(input: {
  signal?: AbortSignal
  onActivity?: () => void
  onEvent: (type: string, properties: Record<string, unknown>) => void
}) {
  const controller = new AbortController()
  const stop = () => controller.abort(input.signal?.reason)
  input.signal?.addEventListener("abort", stop, { once: true })
  let timer: ReturnType<typeof setTimeout> | undefined
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
  const arm = (ms: number) => {
    clearTimeout(timer)
    timer = setTimeout(() => controller.abort(new DOMException("Machine event stream timed out", "TimeoutError")), ms)
  }
  try {
    input.signal?.throwIfAborted()
    arm(fleetStreamConnectTimeoutMs)
    const response = await abortable(controlApiFetch("/api/machines/events", {
      headers: { Accept: "text/event-stream" },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) {
        void response.body?.cancel().catch(() => {})
        controller.signal.throwIfAborted()
      }
      return response
    }), controller.signal)
    await abortable(expectResponse(response, { error: "Machine event stream failed" }), controller.signal)
    if (!response.body) throw new Error("Machine event stream returned no body")
    reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let connected = false
    while (true) {
      const chunk = await abortable(reader.read(), controller.signal)
      controller.signal.throwIfAborted()
      if (chunk.done) break
      if (!chunk.value.length) continue
      input.onActivity?.()
      if (connected) arm(fleetStreamIdleTimeoutMs)
      buffer += decoder.decode(chunk.value, { stream: true })
      const blocks = buffer.split("\n\n")
      buffer = blocks.pop() ?? ""
      for (const block of blocks) {
        const data = block.split("\n").find((line) => line.startsWith("data: "))?.slice(6)
        if (!data) continue
        let type: string
        let properties: Record<string, unknown>
        try {
          const event = jsonObject(JSON.parse(data) as unknown)
          const payload = jsonObject(event.payload)
          type = jsonString(payload.type)
          properties = jsonObject(payload.properties)
        } catch {
          // A later authoritative snapshot reconciles malformed event gaps.
          continue
        }
        if (type === "server.connected") {
          connected = true
          arm(fleetStreamIdleTimeoutMs)
        }
        input.onEvent(type, properties)
      }
    }
  } finally {
    clearTimeout(timer)
    input.signal?.removeEventListener("abort", stop)
    controller.abort()
    if (reader) {
      await reader.cancel().catch(() => {})
      reader.releaseLock()
    }
  }
}
